import * as React from 'react'
import { cn } from '@/lib/utils'
import type { Listing } from '@/lib/types'
import { isLikelyMonthlyPrice } from './helpers'

// Shared price-breakdown math + row building + the "what you'll pay" block
// used by the liked card, the notifications review card and (derivation only)
// the detail page's pay card. All values come from the REAL price_breakdown —
// rows with no data show an em dash or are omitted; values are never invented.
// Listings carry no transaction_type, so the shared isLikelyMonthlyPrice
// heuristic gates all monthly framing ('/mo', rent row, first-month total).

export interface PriceBreakdownData {
  /** Base price (monthly rent when `monthly`). */
  price: number
  monthly: boolean
  utilities?: number
  serviceCharges?: number
  parking?: number
  internet?: number
  depositMonths?: number
  /** Explicit deposit_amount, or deposit_months × price when derivable. */
  depositAmount?: number
  /** First-month move-in total: price + utilities (+ extras when opted in) + deposit. */
  firstMonth: number
}

export interface DerivePriceBreakdownOptions {
  /**
   * Also derive the deposit from months × price for sale-priced listings
   * (pay-card behavior). Default: derive only when the price reads as monthly
   * rent — months × a sale price is meaningless.
   */
  deriveDepositWhenSale?: boolean
  /** Count service charges / parking / internet into firstMonth (pay-card). */
  extrasInFirstMonth?: boolean
}

export function derivePriceBreakdown(
  listing: Pick<Listing, 'price' | 'price_breakdown'>,
  opts: DerivePriceBreakdownOptions = {}
): PriceBreakdownData {
  const pb = listing.price_breakdown
  const monthly = isLikelyMonthlyPrice(listing.price)
  const depositAmount =
    pb?.deposit_amount ??
    ((monthly || opts.deriveDepositWhenSale) && pb?.deposit_months
      ? pb.deposit_months * listing.price
      : undefined)
  const firstMonth =
    listing.price +
    (pb?.utilities_monthly || 0) +
    (opts.extrasInFirstMonth
      ? (pb?.service_charges_monthly || 0) + (pb?.parking_monthly || 0) + (pb?.internet_monthly || 0)
      : 0) +
    (depositAmount || 0)
  return {
    price: listing.price,
    monthly,
    utilities: pb?.utilities_monthly,
    serviceCharges: pb?.service_charges_monthly,
    parking: pb?.parking_monthly,
    internet: pb?.internet_monthly,
    depositMonths: pb?.deposit_months,
    depositAmount,
    firstMonth,
  }
}

export interface PriceRowLabels {
  monthlyRent: string
  utilities: string
  /** Fully formatted — already includes the "(N months)" suffix when applicable. */
  deposit: string
  // Extras rows render only when the surface provides a label for them.
  serviceCharges?: string
  parking?: string
  internet?: string
}

// Row set per surface. Monthly listings always show rent / utilities /
// deposit (missing values as '—') with any labeled extras in between; sale
// listings show only rows with real data, or nothing when `saleRows` is off.
export function buildPriceRows(
  d: PriceBreakdownData,
  labels: PriceRowLabels,
  fmt: (n: number) => string,
  opts: { saleRows?: boolean } = {}
): Array<[string, string]> {
  const extras: Array<[string, string]> = []
  if (labels.serviceCharges && d.serviceCharges) extras.push([labels.serviceCharges, fmt(d.serviceCharges)])
  if (labels.parking && d.parking) extras.push([labels.parking, fmt(d.parking)])
  if (labels.internet && d.internet) extras.push([labels.internet, fmt(d.internet)])
  if (d.monthly) {
    return [
      [labels.monthlyRent, fmt(d.price)],
      [labels.utilities, d.utilities ? fmt(d.utilities) : '—'],
      ...extras,
      [labels.deposit, d.depositAmount ? fmt(d.depositAmount) : '—'],
    ]
  }
  if (!opts.saleRows) return []
  return [
    ...(d.utilities ? ([[labels.utilities, fmt(d.utilities)]] as Array<[string, string]>) : []),
    ...extras,
    ...(d.depositAmount ? ([[labels.deposit, fmt(d.depositAmount)]] as Array<[string, string]>) : []),
  ]
}

export interface PriceBreakdownBlockProps {
  data: PriceBreakdownData
  rows: Array<[string, string]>
  fmt: (n: number) => string
  /** Formatted price per m²; the block is omitted when undefined. */
  perM2Value?: string
  labels: {
    perMo: string
    perM2: string
    totalFirstMonth: string
  }
}

// Presentational block — design liked.jsx PriceBreakdown (lines 95-128):
// big mono price (+ '/mo'), per-m² corner, breakdown rows, first-month total.
export function PriceBreakdownBlock({ data, rows, fmt, perM2Value, labels }: PriceBreakdownBlockProps) {
  return (
    <div className="mt-3 rounded-shape-md bg-surface-low px-[14px] pb-3 pt-[11px]">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-baseline gap-[5px]">
          <span className="font-mono text-[22px] font-bold text-primary">{fmt(data.price)}</span>
          {data.monthly && <span className="text-[12px] text-ink-muted">{labels.perMo}</span>}
        </div>
        {perM2Value != null && (
          <div className="text-right">
            {/* Was 9.5px — below any readable floor on a phone. */}
            <div className="text-[11px] font-bold tracking-[0.8px] text-ink-muted sm:text-[9.5px]">{labels.perM2}</div>
            <div className="font-mono text-[13px] font-semibold text-ink sm:text-[12.5px]">{perM2Value}</div>
          </div>
        )}
      </div>
      {rows.length > 0 && (
        <div className="mt-2">
          {rows.map(([k, v], i) => (
            <div
              key={k}
              className={cn(
                'flex items-center justify-between py-[6px]',
                i < rows.length - 1 && 'border-b border-outline-variant'
              )}
            >
              <span className="text-[13.5px] text-ink-secondary sm:text-[12.5px]">{k}</span>
              <span className="font-mono text-[13.5px] font-semibold text-ink sm:text-[12.5px]">{v}</span>
            </div>
          ))}
        </div>
      )}
      {data.monthly && (
        <div className="mt-[3px] flex items-center justify-between border-t border-outline pt-[9px]">
          <span className="text-[13px] font-semibold text-ink">{labels.totalFirstMonth}</span>
          <span className="font-mono text-[15px] font-bold text-primary">{fmt(data.firstMonth)}</span>
        </div>
      )}
    </div>
  )
}
